import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { apiErr } from "@/lib/api";

export default function PublicForm() {
  const { publicId } = useParams();
  const [form, setForm] = useState(null);
  const [err, setErr] = useState(null);
  const [ans, setAns] = useState({});
  const [done, setDone] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/forms/public/${publicId}`).then((r) => setForm(r.data)).catch((e) => setErr(apiErr(e, "Questionnaire indisponible")));
  }, [publicId]);

  const setV = (qid, v) => setAns({ ...ans, [qid]: v });
  const toggleMulti = (qid, opt) => {
    const cur = ans[qid] || [];
    setV(qid, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };

  const submit = async () => {
    for (const q of form.questions) {
      if (q.required && !ans[q.id]) { toast.error(`Réponse requise : ${q.label}`); return; }
    }
    setSending(true);
    try { await api.post(`/forms/public/${publicId}/submit`, { answers: ans }); setDone(true); }
    catch (e) { toast.error(apiErr(e)); } finally { setSending(false); }
  };

  if (err) return <div className="min-h-screen bg-lisungi-bg flex items-center justify-center p-6"><div className="bg-white rounded-2xl border border-[#E5E7EB] p-8 text-center max-w-md"><p className="text-navy font-semibold">{err}</p></div></div>;
  if (!form) return <div className="min-h-screen bg-lisungi-bg flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-navy border-t-lisungi-yellow animate-spin" /></div>;
  if (done) return <div className="min-h-screen bg-lisungi-bg flex items-center justify-center p-6"><div className="bg-white rounded-2xl border border-[#E5E7EB] p-8 text-center max-w-md"><Logo variant="dark" /><p className="text-navy font-semibold mt-6 text-lg">Merci ! Votre réponse a été enregistrée.</p></div></div>;

  return (
    <div className="min-h-screen bg-lisungi-bg py-10 px-5">
      <div className="max-w-2xl mx-auto">
        <div className="bg-navy rounded-t-2xl p-6"><Logo variant="light" /></div>
        <div className="bg-white border border-[#E5E7EB] p-6">
          <h1 className="font-serif text-2xl font-bold text-navy">{form.title}</h1>
          {form.intro && <p className="text-muted2 mt-2">{form.intro}</p>}
          {form.instructions && <p className="text-sm text-ink mt-2 bg-lisungi-bg rounded-lg p-3">{form.instructions}</p>}
        </div>
        <div className="bg-white border-x border-b border-[#E5E7EB] rounded-b-2xl p-6 space-y-5">
          {form.questions.map((q) => (
            <div key={q.id} data-testid={`pubq-${q.id}`}>
              <Label className="text-navy font-medium">{q.label} {q.required && <span className="text-red-500">*</span>}</Label>
              <div className="mt-2">
                {q.type === "short" && <Input value={ans[q.id] || ""} onChange={(e) => setV(q.id, e.target.value)} />}
                {q.type === "paragraph" && <Textarea rows={3} value={ans[q.id] || ""} onChange={(e) => setV(q.id, e.target.value)} />}
                {q.type === "date" && <Input type="date" value={ans[q.id] || ""} onChange={(e) => setV(q.id, e.target.value)} />}
                {q.type === "scale" && <div className="flex gap-2">{[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setV(q.id, n)} className={`h-10 w-10 rounded-full border ${ans[q.id] === n ? "bg-navy text-white border-navy" : "border-[#E5E7EB] text-navy"}`}>{n}</button>)}</div>}
                {q.type === "single" && q.options.map((o) => <label key={o} className="flex items-center gap-2 py-1 text-sm cursor-pointer"><input type="radio" name={q.id} checked={ans[q.id] === o} onChange={() => setV(q.id, o)} /> {o}</label>)}
                {q.type === "multiple" && q.options.map((o) => <label key={o} className="flex items-center gap-2 py-1 text-sm cursor-pointer"><input type="checkbox" checked={(ans[q.id] || []).includes(o)} onChange={() => toggleMulti(q.id, o)} /> {o}</label>)}
              </div>
            </div>
          ))}
          <Button onClick={submit} disabled={sending} className="bg-navy text-white rounded-full w-full h-11" data-testid="pub-submit">{sending ? "Envoi…" : "Envoyer mes réponses"}</Button>
          <p className="text-center text-xs text-muted2">Lisungi AI est créé par le LARSICOM.</p>
        </div>
      </div>
    </div>
  );
}
